/**
 * ค้นหาเมนู / พาไป (Wayfinding, A18) — deterministic command palette first
 * (instant, free, no LLM). If nothing matches, an LLM fallback (HumanAI-funded
 * key, menu labels only — no tenant data) suggests the closest pages. It points
 * you to the page; it never performs the action.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronRight, Sparkles } from 'lucide-react';
import { searchMenu, type MenuEntry } from '@payroll/shared';
import { PageLayout } from '../components/layout/PageLayout';
import { useAuth } from '../hooks/useAuth';
import { useWayfind } from '../hooks';
import { MENU, menuAllowedForRole } from '../lib/menu';

export function Find() {
  const { profile } = useAuth();
  const [params] = useSearchParams();
  const [q, setQ] = useState(() => params.get('q') ?? '');
  const wayfind = useWayfind();
  const [aiMatches, setAiMatches] = useState<{ label: string; route: string }[]>([]);
  if (!profile) return null;

  const navRole = profile.role === 'MANAGER' ? 'HR' : profile.role;
  // 🚨 navRole map MANAGER→HR — ต้องกรองด้วย role จริงซ้ำ ไม่งั้นค้นหาเจอหน้าเงินเดือน
  // แล้วกดเข้าไปเด้ง 403 (หลังบ้านปิดด้วย requireHqOnly แล้ว)
  const visibleMenu = MENU.filter((m) => menuAllowedForRole(m.route, profile.role));
  const roleMenu = visibleMenu.filter((m) => m.roles.includes(navRole));
  const results = searchMenu(visibleMenu, q, navRole);

  function update(v: string) {
    setQ(v);
    setAiMatches([]); // reset AI suggestions when typing
  }

  async function askAI() {
    if (!q.trim()) return;
    const res = await wayfind.mutateAsync({
      query: q.trim(),
      candidates: roleMenu.map((m: MenuEntry) => ({ label: m.label, route: m.route })),
    });
    setAiMatches(res.matches);
  }

  const noHits = q.trim() !== '' && results.length === 0;

  return (
    <PageLayout title="ค้นหาเมนู / พาไป">
      <div className="space-y-4">
        <div className="flex items-center gap-2 card px-3 py-2">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            value={q}
            onChange={(e) => update(e.target.value)}
            placeholder="พิมพ์ชื่อเมนู เช่น ขอลา, เงินเดือน, อนุมัติ…"
            className="flex-1 outline-none bg-transparent text-sm"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          {results.map((m) => (
            <Link key={m.route} to={m.route} className="card p-3 flex items-center gap-3 hover:bg-gray-50 transition">
              <span className="text-sm flex-1">{m.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </Link>
          ))}
        </div>

        {noHits && aiMatches.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 px-1">ไม่พบเมนูที่ตรงกับ “{q}”</p>
            <button onClick={askAI} disabled={wayfind.isPending} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Sparkles className="w-4 h-4" /> {wayfind.isPending ? 'กำลังค้นด้วย AI…' : 'ให้ AI ช่วยหา'}
            </button>
          </div>
        )}

        {aiMatches.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-brand-700 px-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI แนะนำ</p>
            {aiMatches.map((m) => (
              <Link key={m.route} to={m.route} className="card p-3 flex items-center gap-3 hover:bg-gray-50 transition">
                <span className="text-sm flex-1">{m.label}</span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
